class TabGroup extends HTMLElement {
  /**
   * Constructor for TabGroup class.
   * Calls the superclass constructor.
   * Does nothing else.
   */
  constructor() {
    super();
  }

  /**
   * Lifecycle callback invoked when the component is inserted into a document.
   * Logs a message to the console and calls the `_init` method.
   */
  connectedCallback() {
    console.log("TabGroup connected");
    this._init();
  }

  /**
   * Initializes the component.
   * Calls methods to set the elements and bind the events.
   * @private
   */
  _init() {
    this._setElements();
    this._bindEvents();
  }

  /**
   * Sets the elements.
   * Queries the DOM for the button tabs and the content tabs and assigns them to the respective class properties.
   * @private
   */
  _setElements() {
    this.buttonTabs = this.querySelectorAll(".tab-button");
    this.contentTabs = this.querySelectorAll(".tab-content");
  }

  /**
   * Binds the events.
   * Adds an event listener to each button tab to listen for a "click" event and calls the `_changeActiveTab` method when triggered.
   * @private
   */
  _bindEvents() {
    this.buttonTabs.forEach((button) => {
      button.addEventListener("click", this._changeActiveTab.bind(this));
    });
  }

  /**
   * Handles the click event on a button tab.
   * Resets the style of all tabs and then sets the style of the
   * clicked tab to the active style. Also shows the content tab
   * that corresponds to the clicked button tab.
   * @param {Event} e - The click event.
   * @private
   */
  _changeActiveTab(e) {
    this._resetTabs();

    e.currentTarget.classList.remove("button--secondary");

    this._showElement(
      this.querySelector(`#${e.currentTarget.dataset.targetId}`)
    );
  }

  /**
   * Resets the tabs to their initial state.
   * Adds the "button--secondary" class to all button tabs and hides all content tabs.
   * @private
   */
  _resetTabs() {
    this.buttonTabs.forEach((button) => {
      button.classList.add("button--secondary");
    });
    this.contentTabs.forEach((content) => {
      this._hiddeElement(content);
    });
  }

  /**
   * Hides the specified element by adjusting its CSS classes.
   * Removes classes that make the element visible and adds
   * classes to make it invisible, absolutely positioned, and
   * fully transparent.
   *
   * @param {HTMLElement} element - The DOM element to hide.
   * @private
   */
  _hiddeElement(element) {
    element.classList.remove("tw-opacity-100", "tw-relative", "tw-visible");
    element.classList.add("tw-opacity-0", "tw-absolute", "tw-invisible");
  }

  /**
   * Shows the specified element by adjusting its CSS classes.
   * Removes classes that make the element hidden and adds
   * classes to make it visible, relatively positioned, and
   * fully opaque. Uses a timeout to create a fade-in effect.
   *
   * @param {HTMLElement} element - The DOM element to show.
   * @private
   */
  _showElement(element) {
    element.classList.remove("tw-opacity-0", "tw-absolute", "tw-invisible");
    setTimeout(() => {
      element.classList.add("tw-opacity-100", "tw-relative", "tw-visible");
    }, 500);
  }

  /**
   * Lifecycle callback invoked when the component is removed from a document.
   * Logs a message to the console.
   */
  disconnectedCallback() {
    console.log("TabGroup disconnected");
  }
}

customElements.define("tab-group", TabGroup);
